import { motion } from "framer-motion";

type State = "dim" | "lit" | "active";

interface Props {
  index: number;
  state: State;
}

/**
 * Equaliser glyph, one per discipline.
 * Same architectural grammar across all eight cells: vertical hairline bars,
 * 1px caps, fixed 40×16 footprint. Variety comes from bar count, height
 * pattern, and the animation cadence so each discipline has its own rhythm.
 */

// Eight distinct bar-height patterns + animation profiles.
// Heights are in viewBox units (0-16). Each profile shapes a different "shape"
// of signal: ramp, wave, plateau, etc.
type Profile = {
  heights: number[];
  // animation pattern: how the bars breathe when active/lit
  pattern: "wave" | "pulse" | "ripple" | "stagger" | "alternate" | "sweep" | "drift" | "compound";
  // base cadence in seconds
  duration: number;
};

const PROFILES: Profile[] = [
  // 0 STRATEGY, ascending ramp (a bet building conviction)
  { heights: [4, 6, 8, 10, 12, 14], pattern: "sweep", duration: 1.6 },
  // 1 EXPERIENCE, wave (a continuous journey)
  { heights: [6, 10, 13, 11, 8, 12, 9, 5], pattern: "wave", duration: 1.4 },
  // 2 PRODUCT, plateau with a peak (structured surfaces)
  { heights: [7, 9, 11, 13, 11, 9, 7], pattern: "stagger", duration: 1.8 },
  // 3 CONTENT, paragraph-like rhythm (varied measure)
  { heights: [10, 6, 13, 8, 11, 5, 9], pattern: "drift", duration: 2.0 },
  // 4 SYSTEMS, even lattice (consistent grid)
  { heights: [9, 9, 9, 9, 9, 9, 9, 9], pattern: "alternate", duration: 1.2 },
  // 5 DATA / AI, sparse with one tall signal node
  { heights: [5, 8, 6, 14, 7, 9, 5], pattern: "pulse", duration: 1.1 },
  // 6 AUTOMATION, repeating triplets (cyclic cadence)
  { heights: [6, 11, 6, 11, 6, 11, 6, 11], pattern: "ripple", duration: 1.3 },
  // 7 IMPACT, compounding curve (leverage)
  { heights: [3, 5, 7, 10, 13, 15], pattern: "compound", duration: 1.7 },
];

// Generate per-bar scaleY keyframes based on animation pattern.
function getBarAnim(
  pattern: Profile["pattern"],
  i: number,
  count: number,
  active: boolean
): { values: number[]; delay: number } {
  const baseLow = active ? 0.55 : 0.7;
  const baseHigh = active ? 1 : 0.92;

  switch (pattern) {
    case "wave": {
      // sine wave traveling across bars
      const phase = (i / count) * Math.PI * 2;
      return {
        values: [
          baseLow + (Math.sin(phase) * 0.5 + 0.5) * (baseHigh - baseLow),
          baseHigh,
          baseLow + (Math.cos(phase) * 0.5 + 0.5) * (baseHigh - baseLow),
        ],
        delay: i * 0.06,
      };
    }
    case "pulse":
      // central bar pulses bigger, others minor
      return {
        values: i === Math.floor(count / 2) ? [baseLow, baseHigh, baseLow] : [baseHigh, baseLow, baseHigh],
        delay: 0,
      };
    case "ripple":
      // outward ripple
      return {
        values: [baseLow, baseHigh, baseLow],
        delay: Math.abs(i - count / 2) * 0.08,
      };
    case "stagger":
      return {
        values: [baseLow, baseHigh, baseLow],
        delay: i * 0.1,
      };
    case "alternate":
      return {
        values: i % 2 === 0 ? [baseLow, baseHigh, baseLow] : [baseHigh, baseLow, baseHigh],
        delay: 0,
      };
    case "sweep":
      return {
        values: [baseLow, baseHigh, baseLow],
        delay: i * 0.12,
      };
    case "drift":
      return {
        values: [
          baseLow + Math.random() * 0.2,
          baseHigh,
          baseLow + Math.random() * 0.2,
        ],
        delay: i * 0.09,
      };
    case "compound":
      // each bar holds slightly longer the further right it sits
      return {
        values: [baseLow, baseHigh, baseHigh - 0.05],
        delay: i * 0.14,
      };
  }
}

const DisciplineGlyph = ({ index, state }: Props) => {
  const color =
    state === "active"
      ? "hsl(var(--accent))"
      : state === "lit"
      ? "hsl(var(--foreground) / 0.7)"
      : "hsl(var(--foreground) / 0.3)";

  const profile = PROFILES[index] ?? PROFILES[0];
  const { heights, pattern, duration } = profile;

  // Layout: center bars within a fixed footprint with consistent gap
  const W = 56;
  const H = 18;
  const barWidth = 1.6;
  const gap = 2;
  const totalWidth = heights.length * barWidth + (heights.length - 1) * gap;
  const offsetX = (W - totalWidth) / 2;

  const animating = state !== "dim";

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      fill="none"
      preserveAspectRatio="xMidYMid meet"
    >
      {heights.map((h, i) => {
        const x = offsetX + i * (barWidth + gap);
        const y = H - h;
        const { values, delay } = getBarAnim(pattern, i, heights.length, state === "active");

        return (
          <motion.rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={h}
            rx={0.5}
            fill={color}
            style={{ transformOrigin: `${x + barWidth / 2}px ${H}px` }}
            initial={{ scaleY: 0.8 }}
            animate={animating ? { scaleY: values } : { scaleY: 0.85 }}
            transition={
              animating
                ? {
                    duration,
                    delay,
                    repeat: Infinity,
                    ease: [0.25, 0.4, 0.25, 1],
                  }
                : { duration: 0.4 }
            }
          />
        );
      })}
    </svg>
  );
};

export default DisciplineGlyph;
